import React, { useState } from 'react';
import styled from 'styled-components';
import { postUploadRealPrice } from 'utils/api/admin';

const TYPE_OPTIONS = [
  { value: '1', label: '매매 (estate_real_type1_price)' },
  { value: '2', label: '전월세 (estate_real_type2_price)' },
];

const ENCODING_OPTIONS = [
  { value: 'cp949', label: 'CP949 (MOLIT 기본값)' },
  { value: 'utf-8', label: 'UTF-8' },
];

const RealPriceUpload = () => {
  const [file, setFile] = useState(null);
  const [type, setType] = useState('1');
  const [encoding, setEncoding] = useState('cp949');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
    setResult(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('CSV 파일을 선택해주세요.');
      return;
    }
    if (
      !window.confirm(
        '업로드한 CSV에 포함된 계약년월(ctr_ymd)에 해당하는 기존 데이터는 삭제 후 재삽입됩니다.\n계속하시겠습니까?',
      )
    ) {
      return;
    }
    setLoading(true);
    setResult(null);
    setError('');
    try {
      const res = await postUploadRealPrice({ file, type, encoding });
      setResult(res.results);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        '업로드에 실패했습니다.';
      setError(Array.isArray(msg) ? msg.join(', ') : msg);
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '-';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(2)} MB`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  return (
    <Container>
      <Header>
        <Title>실거래가 CSV 업로드</Title>
      </Header>
      <Desc>
        국토교통부 실거래가 공개시스템 (
        <a
          href="https://rt.molit.go.kr"
          target="_blank"
          rel="noopener noreferrer"
        >
          rt.molit.go.kr
        </a>
        )에서 내려받은 CSV를 업로드해 <code>estate_real_type1_price</code>(매매)
        또는 <code>estate_real_type2_price</code>(전월세) 테이블에 적재합니다.
      </Desc>
      <Notice>
        <strong>⚠ 주의:</strong> 업로드한 CSV에 포함된 <b>계약년월</b>에
        해당하는 기존 데이터는 삭제된 뒤 새 데이터로 교체됩니다. (해당 월 이외의
        데이터는 보존)
      </Notice>

      <Form onSubmit={handleSubmit}>
        <Field>
          <Label>거래 유형</Label>
          <Select value={type} onChange={(e) => setType(e.target.value)}>
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field>
          <Label>파일 인코딩</Label>
          <Select
            value={encoding}
            onChange={(e) => setEncoding(e.target.value)}
          >
            {ENCODING_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field>
          <Label>CSV 파일 (최대 100MB)</Label>
          <FileInput
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
          />
          {file && (
            <FileInfo>
              선택됨: <b>{file.name}</b> ({formatBytes(file.size)})
            </FileInfo>
          )}
        </Field>

        <SubmitBtn type="submit" disabled={loading || !file}>
          {loading ? '업로드 중... (수십 초~수 분 소요)' : '업로드'}
        </SubmitBtn>
      </Form>

      {error && <ErrorBox>{error}</ErrorBox>}

      {result && (
        <ResultBox>
          <ResultTitle>업로드 완료</ResultTitle>
          <ResultRow>
            <span>대상 테이블</span>
            <b>
              {result.type === '1'
                ? 'estate_real_type1_price (매매)'
                : 'estate_real_type2_price (전월세)'}
            </b>
          </ResultRow>
          <ResultRow>
            <span>파일명</span>
            <b>{result.fileName}</b>
          </ResultRow>
          <ResultRow>
            <span>교체된 월 ({result.monthsReplaced?.length ?? 0}개)</span>
            <b>{(result.monthsReplaced || []).join(', ') || '-'}</b>
          </ResultRow>
          <ResultRow>
            <span>삭제 행 수</span>
            <b>{(result.deletedCount ?? 0).toLocaleString()}</b>
          </ResultRow>
          <ResultRow>
            <span>삽입 행 수</span>
            <b>{(result.insertedCount ?? 0).toLocaleString()}</b>
          </ResultRow>
          {result.parseWarnings && result.parseWarnings.length > 0 && (
            <WarningBox>
              <WarningTitle>
                파싱 경고 ({result.parseWarnings.length})
              </WarningTitle>
              {result.parseWarnings.map((w, idx) => (
                <div key={idx}>
                  · row {w.row}: {w.message}
                </div>
              ))}
            </WarningBox>
          )}
        </ResultBox>
      )}
    </Container>
  );
};

const Container = styled.div``;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;
const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #232d42;
  margin: 0;
`;
const Desc = styled.p`
  font-size: 13px;
  color: #8f9bba;
  margin: 0 0 12px 0;
  line-height: 1.6;
  code {
    background: #f0edff;
    color: #644ded;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 12px;
  }
  a {
    color: #644ded;
    text-decoration: underline;
  }
`;
const Notice = styled.div`
  background: #fff8e1;
  border: 1px solid #f5d679;
  color: #92722a;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 24px;
  line-height: 1.5;
`;
const Form = styled.form`
  background: #fff;
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 640px;
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #232d42;
`;
const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: #644ded;
  }
`;
const FileInput = styled.input`
  padding: 8px 0;
  font-size: 14px;
`;
const FileInfo = styled.div`
  font-size: 12px;
  color: #644ded;
  margin-top: 4px;
`;
const SubmitBtn = styled.button`
  padding: 12px 24px;
  background: #644ded;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
  align-self: flex-start;
  &:hover:not(:disabled) {
    background: #5338d4;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const ErrorBox = styled.div`
  margin-top: 20px;
  padding: 16px;
  background: #ffecec;
  border: 1px solid #dc3545;
  color: #dc3545;
  border-radius: 8px;
  font-size: 13px;
  max-width: 640px;
  white-space: pre-wrap;
`;
const ResultBox = styled.div`
  margin-top: 20px;
  padding: 24px;
  background: #fff;
  border: 1px solid #d5ead0;
  border-radius: 12px;
  max-width: 640px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
`;
const ResultTitle = styled.h3`
  margin: 0 0 16px 0;
  color: #2a7b3a;
  font-size: 16px;
`;
const ResultRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 13px;
  border-bottom: 1px dashed #eee;
  &:last-child {
    border-bottom: none;
  }
  span {
    color: #8f9bba;
  }
  b {
    color: #232d42;
    text-align: right;
    max-width: 70%;
    word-break: break-all;
  }
`;
const WarningBox = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: #fff8e1;
  border-radius: 8px;
  font-size: 12px;
  color: #92722a;
  line-height: 1.6;
`;
const WarningTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

export default RealPriceUpload;
